import React, { useRef, useState } from "react";
import { useAuth } from "@/admin/AuthContext";
import { Upload, Loader2, X, Link as LinkIcon, Image as ImageIcon, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import CropModal from "@/admin/components/CropModal";
import { resolveImageUrl as toAbsoluteUrl } from "@/lib/media";

const MAX_MB = 5;

/**
 * Reusable image uploader with URL input fallback + Media Library picker.
 * Props:
 *   value       — current URL string
 *   onChange    — (url) => void
 *   testid      — data-testid prefix
 *   openLibrary — () => void (parent should render a MediaLibraryModal)
 */
export const ImageUploader = ({ value, onChange, testid = "image-uploader", openLibrary, defaultAspect = "free" }) => {
  const { authAxios } = useAuth();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pendingFile, setPendingFile] = useState(null); // file waiting to be cropped

  const doUpload = async (file) => {
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Ukuran melebihi ${MAX_MB} MB`);
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    setProgress(0);
    try {
      const r = await authAxios.post("/admin/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      onChange(r.data.url);
      toast.success("Gambar berhasil di-upload");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(d || "Gagal upload gambar");
    } finally {
      setUploading(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  // Called after user picks a file — open crop dialog first (GIFs skip crop
  // because canvas conversion would flatten animation).
  const handleFile = (file) => {
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Ukuran melebihi ${MAX_MB} MB`);
      return;
    }
    if (file.type === "image/gif") {
      doUpload(file);
      return;
    }
    setPendingFile(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const clear = () => onChange("");

  return (
    <div className="space-y-2" data-testid={testid}>
      {/* URL text input (also editable manually) */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <LinkIcon className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="/api/assets/... atau https://..."
            data-testid={`${testid}-url-input`}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
          />
        </div>
        {value ? (
          <button
            type="button"
            onClick={clear}
            data-testid={`${testid}-clear`}
            className="h-9 w-9 shrink-0 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center"
            title="Kosongkan"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {/* Drop zone + buttons */}
      <div
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        className="border-2 border-dashed border-slate-200 rounded-xl bg-slate-50 hover:border-blue-300 hover:bg-blue-50/40 transition p-4 flex items-center gap-3"
      >
        {value ? (
          <img
            src={toAbsoluteUrl(value)}
            alt="preview"
            data-testid={`${testid}-preview`}
            className="h-16 w-16 object-cover rounded-lg border border-slate-200 bg-white"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className="h-16 w-16 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-400 shrink-0">
            <ImageIcon className="h-6 w-6" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-xs text-slate-500 leading-relaxed">
            Drag &amp; drop gambar di sini, atau pilih file (JPG / PNG / WebP / GIF, max {MAX_MB} MB).
            Auto-resize ke 1600px.
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <button
              type="button"
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
              data-testid={`${testid}-choose`}
              className="inline-flex items-center gap-1.5 bg-blue-800 hover:bg-blue-900 disabled:opacity-60 text-white text-xs font-medium px-3 py-1.5 rounded-full"
            >
              {uploading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="h-3.5 w-3.5" />
              )}
              {uploading ? `Mengunggah ${progress}%` : "Pilih File"}
            </button>
            {openLibrary && (
              <button
                type="button"
                onClick={openLibrary}
                data-testid={`${testid}-library`}
                className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:border-blue-300 hover:text-blue-800 text-slate-600 text-xs font-medium px-3 py-1.5 rounded-full"
              >
                <FolderOpen className="h-3.5 w-3.5" />
                Media Library
              </button>
            )}
          </div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid={`${testid}-file-input`}
        />
      </div>

      {pendingFile && (
        <CropModal
          file={pendingFile}
          defaultAspect={defaultAspect}
          onCancel={() => {
            setPendingFile(null);
            if (inputRef.current) inputRef.current.value = "";
          }}
          onCropped={async (finalFile) => {
            setPendingFile(null);
            await doUpload(finalFile);
          }}
        />
      )}
    </div>
  );
};

/**
 * Modal that lists uploaded assets. Click one → returns its URL via onPick.
 */
export const MediaLibraryModal = ({ open, onClose, onPick }) => {
  const { authAxios } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const load = React.useCallback(() => {
    setLoading(true);
    authAxios
      .get("/admin/uploads/library?limit=500")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => toast.error("Gagal memuat media"))
      .finally(() => setLoading(false));
  }, [authAxios]);

  React.useEffect(() => {
    if (open) load();
  }, [open, load]);

  if (!open) return null;

  const filtered = q
    ? items.filter((it) => it.filename.toLowerCase().includes(q.toLowerCase()))
    : items;

  const humanSize = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(2)} MB`;
  };

  const handleDelete = async (filename, e) => {
    e.stopPropagation();
    if (!window.confirm(`Hapus file "${filename}" secara permanen?`)) return;
    try {
      await authAxios.delete(`/admin/uploads/${encodeURIComponent(filename)}`);
      toast.success("File dihapus");
      setItems((cur) => cur.filter((x) => x.filename !== filename));
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="media-library-modal"
    >
      <div
        className="bg-white rounded-3xl w-full max-w-5xl max-h-[90vh] shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-slate-100 flex items-center justify-between gap-4">
          <div>
            <h3 className="font-heading text-xl font-bold text-slate-900">Media Library</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {items.length} file — klik untuk pakai, hover untuk hapus
            </p>
          </div>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari nama file..."
            className="ml-auto max-w-xs px-3 py-2 border border-slate-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
          />
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="p-12 flex items-center justify-center text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-400 text-sm border border-dashed border-slate-200 rounded-2xl">
              {q ? "Tidak ada file cocok" : "Belum ada file di-upload."}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {filtered.map((it) => (
                <div
                  key={it.filename}
                  onClick={() => {
                    onPick(it.url);
                    onClose();
                  }}
                  data-testid={`media-item-${it.filename}`}
                  className="group relative bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-blue-500 hover:shadow-md cursor-pointer transition"
                >
                  <div className="aspect-square bg-slate-50">
                    <img
                      src={toAbsoluteUrl(it.url)}
                      alt={it.filename}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-2">
                    <div className="text-[10px] font-mono text-slate-500 truncate" title={it.filename}>
                      {it.filename}
                    </div>
                    <div className="text-[10px] text-slate-400">{humanSize(it.size)}</div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => handleDelete(it.filename, e)}
                    data-testid={`media-delete-${it.filename}`}
                    className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-white/90 opacity-0 group-hover:opacity-100 hover:bg-red-50 text-red-600 flex items-center justify-center shadow"
                    title="Hapus permanen"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageUploader;
